import React from "react";
import { X, Loader2 } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { SearchableSelect } from "@/components/ui";
import { ATTENDANCE_STATUS, ATTENDANCE_STATUS_LABELS } from "../../constants/hrConstants";

export function MarkAttendanceModal({ isOpen, onClose, employees, formData, formErrors, onFieldChange, onSubmit, isSaving, isEditing }) {
  const { user } = useAuthStore();
  const isHR = ["owner", "hr", "accountant", "verve-admin"].includes(String(user?.role).toLowerCase());

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-[15px] font-bold text-slate-800">
            {isEditing ? "Update Attendance" : "Mark Attendance"}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Employee Select */}
          <div>
            {isHR && !isEditing ? (
              <SearchableSelect
                label="Employee *"
                placeholder="Search employee..."
                options={employees.map(e => ({
                  value: e.id,
                  label: `${e.name} (${e.id})${e.status?.toLowerCase() === 'inactive' ? ' — Inactive' : ''}`,
                  disabled: e.status?.toLowerCase() === 'inactive',
                  ...e,
                }))}
                value={formData.employeeId}
                onChange={(val) => {
                  const emp = employees.find(e => e.id === val);
                  onFieldChange("employeeId", val);
                  onFieldChange("employeeName", emp?.name || "");
                }}
                error={formErrors.employeeId}
              />
            ) : (
              <div>
                <label className="label">Employee *</label>
                <input
                  type="text"
                  className="input bg-slate-50 cursor-not-allowed"
                  value={formData.employeeName ? `${formData.employeeName} (${formData.employeeId})` : formData.employeeId || "No Employee Selected"}
                  disabled
                />
                {formErrors.employeeId && <p className="text-red-500 text-[11px] mt-1">{formErrors.employeeId}</p>}
              </div>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="label">Date *</label>
            <input
              type="date" className={`input ${formErrors.date ? "border-red-400" : ""}`}
              value={formData.date || ""} onChange={(e) => onFieldChange("date", e.target.value)}
            />
            {formErrors.date && <p className="text-red-500 text-[11px] mt-1">{formErrors.date}</p>}
          </div>

          {/* Status */}
          <div>
            <label className="label">Status *</label>
            <select
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-400 transition-colors"
              value={formData.status || "present"}
              onChange={(e) => onFieldChange("status", e.target.value)}
            >
              {Object.entries(ATTENDANCE_STATUS).map(([key, v]) => (
                <option key={v} value={v}>{ATTENDANCE_STATUS_LABELS[v]}</option>
              ))}
            </select>
          </div>


          {/* Check In / Out */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Check In</label>
              <input
                type="time" className="input"
                value={formData.checkIn || ""} onChange={(e) => onFieldChange("checkIn", e.target.value)}
              />
            </div>
            <div>
              <label className="label">Check Out</label>
              <input
                type="time" className="input"
                value={formData.checkOut || ""} onChange={(e) => onFieldChange("checkOut", e.target.value)}
              />
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="label">Remarks</label>
            <input
              type="text" className="input"
              placeholder="Optional remarks..."
              value={formData.remarks || ""} onChange={(e) => onFieldChange("remarks", e.target.value)}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary" disabled={isSaving}>Cancel</button>
          <button onClick={onSubmit} className="btn-primary min-w-[110px]" disabled={isSaving}>
            {isSaving ? (
              <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</span>
            ) : isEditing ? "Update" : "Mark Attendance"}
          </button>
        </div>
      </div>
    </div>
  );
}
